import { useEffect, useMemo, useState } from 'react';
import { FolderSearch, PlugZap, RefreshCw, RotateCcw, Save, Server } from 'lucide-react';
import { BrowseUpstreamModelsModal } from './BrowseUpstreamModelsModal';
import { ModelAliasEditor } from './ModelAliasEditor';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button, Modal, Toggle } from '../../ui';
import {
  CanonicalModelKind,
  HealthState,
  ProviderType,
  type Provider,
  type UpsertProviderInput,
} from '../../gql/types';
import { TextField } from '../shared/FormField';
import { SecretField } from '../shared/SecretField';
import { InlineBanner } from '../shared/InlineBanner';
import { PROVIDER_TYPES, providerLabel } from '../shared/providerLabels';
import { ProbeResultBadge } from './ProbeResultBadge';
import { RecentHealthEvents } from './RecentHealthEvents';
import { useProbeProvider } from './useProbeProvider';
import { useMutation, useQuery } from '../../gql/hooks';
import {
  CANONICAL_MODELS_QUERY,
  REFRESH_PROVIDER_MODELS_MUTATION,
  RESET_PROVIDER_HEALTH_MUTATION,
  type CanonicalModelsQueryResult,
  type RefreshProviderModelsMutationResult,
  type RefreshProviderModelsMutationVariables,
  type ResetProviderHealthMutationResult,
  type ResetProviderHealthMutationVariables,
} from '../../gql/operations';

// Create/edit form for an upstream provider (W2). Composes the contract's UpsertProviderInput:
// type, displayName, baseUrl, credentialsRef (a secret POINTER — never a raw secret), enabled,
// weight, maxConcurrency, dataResidency.
//
// DOMAIN CONSTRAINT (DB unique index on provider `type` — at most ONE provider per type):
//   - CREATE: the type select offers only types not already present; taken types are disabled
//     with an explanation.
//   - EDIT: type is read-only (shown, not selectable) — changing it would collide with the index.
//
// EDIT additionally exposes a "Test connection" action (W3b) right where the provider is
// configured, so an admin can validate connectivity/credentials without leaving the form. The
// probe needs a saved id, so it is only offered in edit mode.
//
// Validation: displayName + baseUrl required; baseUrl must parse as an http(s) URL; weight ≥ 1;
// maxConcurrency ≥ 1. Server-side errors are surfaced inline (never silently swallowed).

const DEFAULT_WEIGHT = 100;
const DEFAULT_MAX_CONCURRENCY = 50;

export interface ProviderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing provider to edit, or null to create. */
  provider: Provider | null;
  /** Types already configured — used to constrain the create-mode type select (unique index). */
  takenTypes: ProviderType[];
  loading?: boolean | undefined;
  /** Server-side error from the upsert mutation, surfaced inline. */
  errorMessage?: string | undefined;
  onSubmit: (input: UpsertProviderInput) => void;
  /**
   * Optional "save without closing" hook. Used by the Test connection + Reset & probe
   * buttons to flush the operator's current form edits to the DB BEFORE running their
   * action — otherwise those mutations probe the stale DB row and the operator sees a
   * confusing "I changed it and clicked Test, why is it using the old URL?" experience.
   * Returns the updated Provider so the modal can read its post-save state.
   */
  onSilentSave?: (input: UpsertProviderInput) => Promise<Provider>;
  /**
   * Fired after any alias upsert/delete in the Model aliases section (2026-08-21) so the
   * parent can refetch the fleet — aliases feed eligibility, the docs availability panel,
   * and the probe's model-mismatch check.
   */
  onAliasesChanged?: () => void;
}

const PROVIDER_TYPE_VALUES = Object.values(ProviderType) as string[];

function isProviderType(value: string): value is ProviderType {
  return PROVIDER_TYPE_VALUES.includes(value);
}

/** True when `raw` parses as an absolute http(s) URL. */
function isHttpUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

/** Parse a positive integer field. Returns null when blank/invalid. */
function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isInteger(value)) return null;
  return value;
}

export function ProviderFormModal({
  isOpen,
  onClose,
  provider,
  takenTypes,
  loading = false,
  errorMessage,
  onSubmit,
  onSilentSave,
  onAliasesChanged,
}: ProviderFormModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isEdit = provider !== null;

  // The first type not already taken — a sensible default for create mode.
  const firstAvailableType = useMemo(
    () => PROVIDER_TYPES.find((t) => !takenTypes.includes(t)) ?? null,
    [takenTypes],
  );

  const [type, setType] = useState<ProviderType | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [credentialsRef, setCredentialsRef] = useState('');
  // Snapshot of what credentialsRef was when the modal opened. The submit handler diffs
  // against this so "field unchanged" omits the key (preserves DB value) while "field
  // cleared" sends `null` (explicit wipe) — without this, a populated initial value would
  // make the "blank means keep" heuristic from the original design wrong.
  const [credentialsRefInitial, setCredentialsRefInitial] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [weight, setWeight] = useState(String(DEFAULT_WEIGHT));
  const [maxConcurrency, setMaxConcurrency] = useState(String(DEFAULT_MAX_CONCURRENCY));
  const [dataResidency, setDataResidency] = useState('');
  const [probePath, setProbePath] = useState('');
  const [defaultCanonicalModelId, setDefaultCanonicalModelId] = useState<string | null>(
    null,
  );
  const [refreshSummary, setRefreshSummary] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  // 2026-07-05: nested "Browse upstream models" dialog. Opens a second Modal above
  // this one via React portal — the outer edit form stays intact underneath, so
  // when the operator closes the browse dialog they're back where they left off
  // without losing form state.
  const [browseOpen, setBrowseOpen] = useState(false);

  const probe = useProbeProvider();

  // Canonical models for the "Default model" picker. Filtered to non-auto + enabled rows so
  // operators can only pick something a request can actually resolve to.
  const canonicalModelsQuery = useQuery<CanonicalModelsQueryResult>(
    CANONICAL_MODELS_QUERY,
  );
  const refreshMutation = useMutation<
    RefreshProviderModelsMutationResult,
    RefreshProviderModelsMutationVariables
  >(REFRESH_PROVIDER_MODELS_MUTATION);
  const resetHealth = useMutation<
    ResetProviderHealthMutationResult,
    ResetProviderHealthMutationVariables
  >(RESET_PROVIDER_HEALTH_MUTATION);
  // Mirrors the refreshMutation summary pattern — render the result line just below the
  // button so the operator gets immediate feedback without waiting for the providers list
  // refetch to land.
  const [resetSummary, setResetSummary] = useState<string | null>(null);

  // (Re)seed the form whenever the modal opens or the edited provider changes; clear any prior probe.
  useEffect(() => {
    if (!isOpen) return;
    setType(provider?.type ?? firstAvailableType);
    setDisplayName(provider?.displayName ?? '');
    setBaseUrl(provider?.baseUrl ?? '');
    // credentialsRef is never read back from the API (it's a secret pointer, not selected); start
    // blank in both modes. Leaving it blank on edit means "don't change the stored pointer".
    {
      const initial = provider?.credentialsRef ?? '';
      setCredentialsRef(initial);
      setCredentialsRefInitial(initial);
    }
    setEnabled(provider?.enabled ?? true);
    setWeight(String(provider?.weight ?? DEFAULT_WEIGHT));
    setMaxConcurrency(String(provider?.maxConcurrency ?? DEFAULT_MAX_CONCURRENCY));
    setDataResidency(provider?.dataResidency ?? '');
    setProbePath(provider?.probePath ?? '');
    setDefaultCanonicalModelId(provider?.defaultCanonicalModel?.id ?? null);
    setRefreshSummary(null);
    setResetSummary(null);
    setAttempted(false);
    resetHealth.reset();
    probe.reset();
    // probe.reset is stable (useCallback); excluded to avoid re-seeding on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, provider, firstAvailableType]);

  const trimmedName = displayName.trim();
  const trimmedUrl = baseUrl.trim();
  const parsedWeight = parsePositiveInt(weight);
  const parsedMaxConcurrency = parsePositiveInt(maxConcurrency);

  const nameError = trimmedName.length === 0 ? 'Enter a display name.' : undefined;
  const urlError =
    trimmedUrl.length === 0
      ? 'Enter a base URL.'
      : !isHttpUrl(trimmedUrl)
        ? 'Enter a valid http(s) URL, e.g. https://api.example.com/v1'
        : undefined;
  const weightError =
    parsedWeight === null || parsedWeight < 1 ? 'Weight must be a whole number ≥ 1.' : undefined;
  const concurrencyError =
    parsedMaxConcurrency === null || parsedMaxConcurrency < 1
      ? 'Max concurrency must be a whole number ≥ 1.'
      : undefined;
  const typeError = type === null ? 'Select a provider type.' : undefined;

  const valid =
    !nameError && !urlError && !weightError && !concurrencyError && !typeError;

  // True when the form's current state differs from the loaded provider. Used by the
  // save-first wrapper so Test connection / Reset & probe operate on the values the
  // operator actually typed instead of the stale DB row.
  const isDirty = useMemo(() => {
    if (!isEdit || !provider) return true;
    return (
      trimmedName !== provider.displayName ||
      trimmedUrl !== provider.baseUrl ||
      parsedWeight !== provider.weight ||
      parsedMaxConcurrency !== provider.maxConcurrency ||
      (dataResidency.trim() || null) !== (provider.dataResidency || null) ||
      (probePath.trim() || null) !== (provider.probePath || null) ||
      defaultCanonicalModelId !== (provider.defaultCanonicalModel?.id ?? null) ||
      enabled !== provider.enabled ||
      // credentialsRef is sent only when the operator actually changed it.
      credentialsRef !== credentialsRefInitial
    );
  }, [
    isEdit,
    provider,
    trimmedName,
    trimmedUrl,
    parsedWeight,
    parsedMaxConcurrency,
    dataResidency,
    probePath,
    defaultCanonicalModelId,
    enabled,
    credentialsRef,
    credentialsRefInitial,
  ]);

  // 2026-06-08: operators can configure multiple providers of the same type. The list
  // appends a small count hint when a type is already in use (e.g. "OpenAI (×2)") so
  // it's obvious you're adding a second instance, but the option stays enabled.
  const typeOptions = useMemo(
    () =>
      PROVIDER_TYPES.map((t) => {
        const existing = takenTypes.filter((taken) => taken === t).length;
        return {
          value: t,
          label: existing > 0 ? `${providerLabel(t)}  ·  ${existing} configured` : providerLabel(t),
        };
      }),
    [takenTypes],
  );

  const buildInput = (): UpsertProviderInput | null => {
    if (!valid || type === null || parsedWeight === null || parsedMaxConcurrency === null) {
      return null;
    }
    const trimmedCredentials = credentialsRef.trim();
    const trimmedResidency = dataResidency.trim();
    const trimmedProbePath = probePath.trim();
    const input: UpsertProviderInput = {
      type,
      displayName: trimmedName,
      baseUrl: trimmedUrl,
      enabled,
      weight: parsedWeight,
      maxConcurrency: parsedMaxConcurrency,
      dataResidency: trimmedResidency === '' ? null : trimmedResidency,
      // Send the override verbatim when set; null wipes the column → backend falls back
      // to the per-type default.
      probePath: trimmedProbePath === '' ? null : trimmedProbePath,
      defaultCanonicalModelId: defaultCanonicalModelId,
      ...credentialsRefUpdate(trimmedCredentials, credentialsRefInitial),
    };
    if (isEdit && provider) input.id = provider.id;
    return input;
  };

  const handleSubmit = () => {
    setAttempted(true);
    const input = buildInput();
    if (input === null) return;
    onSubmit(input);
  };

  // Operators expect Test connection / Reset & probe to use the values currently in the
  // form, not whatever's in the DB. The backend mutations both read from the DB, so the
  // simplest "no surprise" behavior is to save first when the form differs from the loaded
  // provider, then run the action. If the form is already clean, skip the save and run
  // the action directly. Returns true when the action can proceed, false when validation
  // blocked the save (in which case `setAttempted(true)` surfaces the inline errors).
  const persistIfDirty = async (): Promise<boolean> => {
    if (!isEdit || !provider) return true;
    const input = buildInput();
    if (input === null) {
      setAttempted(true);
      return false;
    }
    if (!isDirty || !onSilentSave) return true;
    try {
      await onSilentSave(input);
      return true;
    } catch {
      // The save mutation already surfaces its error via the parent's `upsert.error`
      // banner that renders above the modal body, so we just abort the follow-up.
      return false;
    }
  };

  // Tri-state diff against the loaded value so the GraphQL update reflects what the
  // operator actually intended:
  //   * unchanged → omit the key entirely (changeset doesn't touch the column)
  //   * cleared → send `null` (explicit wipe)
  //   * edited  → send the new value
  function credentialsRefUpdate(
    current: string,
    initial: string,
  ): { credentialsRef?: string | null } {
    if (current === initial) return {};
    if (current === '') return { credentialsRef: null };
    return { credentialsRef: current };
  }

  const canonicalOptions = useMemo(() => {
    const all = canonicalModelsQuery.data?.canonicalModels ?? [];
    // Enabled rows first, disabled below — no point making the operator scroll past
    // "claude-foo — disabled" entries to find a real selection. Within each group keep
    // the backend's order (which is alphabetical with `auto` pinned, but `auto` is
    // filtered out here because operators never pick it as a default).
    const filtered = all.filter((m) => m.kind !== CanonicalModelKind.Auto);
    const enabled = filtered.filter((m) => m.enabled);
    const disabled = filtered.filter((m) => !m.enabled);
    return [...enabled, ...disabled].map((m) => ({
      value: m.id,
      label: m.enabled ? m.name : `${m.name} — disabled`,
      disabled: !m.enabled,
    }));
  }, [canonicalModelsQuery.data]);

  const handleRefreshModels = async () => {
    if (!isEdit || !provider) return;
    setRefreshSummary(null);
    try {
      const result = await refreshMutation.mutate({ id: provider.id });
      const r = result.refreshProviderModels;
      const summaryBits: string[] = [];
      if (r.added.length > 0) summaryBits.push(`${r.added.length} new model(s)`);
      if (r.aliased.length > 0) summaryBits.push(`${r.aliased.length} new alias(es)`);
      if (summaryBits.length === 0) summaryBits.push('already up to date');
      setRefreshSummary(`Refreshed: ${summaryBits.join(', ')}`);
      // Re-fetch canonical models so the default picker sees newly added rows.
      canonicalModelsQuery.refetch();
    } catch (e) {
      setRefreshSummary(
        `Refresh failed: ${e instanceof Error ? e.message : 'unknown error'}`,
      );
    }
  };

  const handleResetHealth = async () => {
    if (!isEdit || !provider) return;
    setResetSummary(null);
    // Persist any unsaved form edits first so the reset + probe operates on the values
    // the operator actually typed (e.g. a new probe_path) rather than the stale DB row.
    if (!(await persistIfDirty())) return;
    try {
      const result = await resetHealth.mutate({ id: provider.id });
      const post = result.resetProviderHealth.health;
      // The mutation runs a fresh probe inside the same call; surface its outcome verbatim
      // so the operator knows whether the reset stuck or whether the upstream is still down.
      const stateLabel = post.state.toLowerCase();
      setResetSummary(
        post.state === HealthState.Healthy
          ? `Reset · counters cleared · post-probe state: ${stateLabel}`
          : `Reset · counters cleared · post-probe state: ${stateLabel} (upstream still failing)`,
      );
    } catch (e) {
      setResetSummary(
        `Reset failed: ${e instanceof Error ? e.message : 'unknown error'}`,
      );
    }
  };

  const handleTestConnection = async () => {
    if (!isEdit || !provider) return;
    if (!(await persistIfDirty())) return;
    void probe.probe(provider.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="wide"
      title={isEdit ? 'Edit provider' : 'Add provider'}
      subtitle="Configure an upstream provider's routing weight, capacity, and credential pointer."
      icon={<Server className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Save className="h-4 w-4" aria-hidden="true" />}
          >
            Save provider
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {errorMessage && (
          <InlineBanner tone="error" testId="provider-form-error">
            Couldn’t save the provider: {errorMessage}
          </InlineBanner>
        )}

        <div className="space-y-1.5">
          <span
            className={`block text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
          >
            Provider type <span aria-hidden="true">*</span>
          </span>
          {isEdit ? (
            // EDIT: type is read-only — changing a configured provider's adapter family
            // would invalidate every model alias attached to it. Operators delete + add
            // a new one to switch adapter classes.
            <div
              data-testid="provider-type-readonly"
              className={`flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${
                isDark
                  ? 'border-white/[0.06] bg-white/[0.02] text-white/70'
                  : 'border-slate-200 bg-slate-100 text-slate-600'
              }`}
            >
              <span>{type ? providerLabel(type) : '—'}</span>
              <span className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                Read-only
              </span>
            </div>
          ) : (
            <AutocompleteSelect
              label="Provider type"
              value={type}
              options={typeOptions}
              onChange={(next) => {
                if (isProviderType(next)) setType(next);
              }}
            />
          )}
          {isEdit ? (
            <p className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
              A provider's type can't change — exactly one provider is allowed per type.
            </p>
          ) : (
            <p className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
              At most one provider per type. Types already configured are unavailable.
            </p>
          )}
        </div>

        <TextField
          label="Display name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. In-House vLLM"
          error={attempted ? nameError : undefined}
        />

        <TextField
          label="Base URL"
          required
          type="url"
          inputMode="url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://api.example.com/v1"
          error={attempted ? urlError : undefined}
        />

        <SecretField
          label="Credentials reference"
          value={credentialsRef}
          onChange={setCredentialsRef}
          placeholder="env:OPENAI_API_KEY  ·  or  ·  raw:sk-…"
          hint={
            isEdit
              ? 'Use `env:VAR` to point at an environment variable (recommended), or `raw:VALUE` / `key:VALUE` to paste a literal API key. Click the eye to reveal, × to clear (sends an explicit wipe on save).'
              : 'Use `env:VAR` to point at an environment variable (recommended), or `raw:VALUE` / `key:VALUE` to paste a literal API key (stored in DB plaintext).'
          }
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Weight"
            required
            type="number"
            min="1"
            inputMode="numeric"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            hint="Relative routing share. Higher gets more traffic."
            error={attempted ? weightError : undefined}
          />
          <TextField
            label="Max concurrency"
            required
            type="number"
            min="1"
            inputMode="numeric"
            value={maxConcurrency}
            onChange={(e) => setMaxConcurrency(e.target.value)}
            hint="Max in-flight requests to this provider."
            error={attempted ? concurrencyError : undefined}
          />
        </div>

        <TextField
          label="Data residency"
          value={dataResidency}
          onChange={(e) => setDataResidency(e.target.value)}
          placeholder="Optional — e.g. au"
          hint="Region code this provider is constrained to. Leave blank if unconstrained."
        />

        <TextField
          label="Probe path"
          value={probePath}
          onChange={(e) => setProbePath(e.target.value)}
          placeholder="Optional — e.g. /health"
          hint="Override for the URL path the periodic + on-demand probe hits. Leave blank to use the per-type default (`/models` OpenAI-compatible, `/v1/models` Anthropic, `/v1beta/models` Gemini)."
        />

        <div className="space-y-1.5">
          <span
            className={`block text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
          >
            Default canonical model
          </span>
          <AutocompleteSelect
            label="Default canonical model"
            value={defaultCanonicalModelId}
            options={canonicalOptions}
            placeholder="None (skip in auto-routing)"
            onChange={(value) => setDefaultCanonicalModelId(value || null)}
          />
          <p className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
            When a client requests <code>auto</code>, this is the model substituted on this
            provider. Leave blank to skip this provider in auto-routing.
          </p>
        </div>

        {isEdit && provider && (
          <div
            className={`flex items-center justify-between rounded-xl border p-4 ${
              isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span
                className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}
              >
                Refresh upstream models
              </span>
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                Pull this provider's model catalog (e.g. Anthropic <code>GET /v1/models</code>)
                and import new entries into the canonical catalog as disabled rows.
              </p>
              {refreshSummary && (
                <p
                  className={`mt-1.5 text-xs font-mono ${
                    refreshSummary.startsWith('Refresh failed')
                      ? isDark
                        ? 'text-rose-300'
                        : 'text-rose-600'
                      : isDark
                        ? 'text-emerald-300'
                        : 'text-emerald-700'
                  }`}
                >
                  {refreshSummary}
                </p>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={refreshMutation.loading}
              onClick={() => void handleRefreshModels()}
              leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
            >
              Refresh
            </Button>
          </div>
        )}

        <div
          className={`flex items-center justify-between rounded-xl border p-4 ${
            isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
          }`}
        >
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
              Routing enabled
            </span>
            <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              Disabled providers are kept but excluded from routing.
            </p>
          </div>
          <Toggle
            checked={enabled}
            onChange={setEnabled}
            label={enabled ? 'Disable provider' : 'Enable provider'}
          />
        </div>

        {/* Test connection + Reset & probe — edit mode only (need a saved id). */}
        {isEdit && provider && (
          <div
            className={`space-y-2 rounded-xl border p-4 ${
              isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span
                className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}
              >
                Connection
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setBrowseOpen(true)}
                  leftIcon={<FolderSearch className="h-4 w-4" aria-hidden="true" />}
                  data-testid="browse-upstream-models"
                  title="Open a live view of the models this upstream is currently serving."
                >
                  Browse models
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={resetHealth.loading}
                  onClick={() => void handleResetHealth()}
                  leftIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                  data-testid="reset-provider-health"
                  title="Clear the failure/success counters, force state back to HEALTHY, and run one fresh probe."
                >
                  Reset &amp; probe
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={probe.loading}
                  onClick={() => void handleTestConnection()}
                  leftIcon={<PlugZap className="h-4 w-4" aria-hidden="true" />}
                >
                  Test connection
                </Button>
              </div>
            </div>
            {isDirty && (
              <p
                className={`text-xs ${
                  isDark ? 'text-amber-300/80' : 'text-amber-700/90'
                }`}
                data-testid="provider-form-dirty-hint"
              >
                Unsaved changes — clicking Test connection or Reset &amp; probe will save
                this form first so the action runs against the values you just typed.
              </p>
            )}
            {resetSummary && (
              <p
                className={`text-xs font-mono ${
                  resetSummary.startsWith('Reset failed')
                    ? isDark
                      ? 'text-rose-300'
                      : 'text-rose-600'
                    : resetSummary.includes('upstream still failing')
                      ? isDark
                        ? 'text-amber-300'
                        : 'text-amber-700'
                      : isDark
                        ? 'text-emerald-300'
                        : 'text-emerald-700'
                }`}
                data-testid="reset-provider-health-summary"
              >
                {resetSummary}
              </p>
            )}
            {probe.error && (
              <InlineBanner tone="error" testId="provider-probe-error" className="mb-0">
                {probe.error}
              </InlineBanner>
            )}
            {probe.result && !probe.error && <ProbeResultBadge result={probe.result} />}
          </div>
        )}

        {/* Model aliases (2026-08-21): canonical model → the literal id sent upstream on
            dispatch (what probes verify). Editable here at last — previously aliases were
            only creatable implicitly via Refresh models, never remappable. */}
        {isEdit && provider && (
          <div
            className={`space-y-3 rounded-xl border p-4 ${
              isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <div>
              <span
                className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}
              >
                Model aliases
              </span>
              <p className={`mt-0.5 text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                What each canonical model dispatches as on this provider. The probe checks
                these ids against the upstream <code className="font-mono">/models</code>{' '}
                list — use <strong>Browse models</strong> to see what it serves live.
              </p>
            </div>
            <ModelAliasEditor
              providerId={provider.id}
              aliases={provider.modelAliases ?? []}
              canonicalModelOptions={canonicalOptions.filter((o) => !o.disabled)}
              onChanged={() => onAliasesChanged?.()}
            />
          </div>
        )}

        {/* Recent health events — in-memory ring buffer from the HealthServer. Surfaced
            here so an operator inspecting "why is this provider degrading?" can read the
            probe + live trace without leaving the edit modal. */}
        {isEdit && provider && (
          <div
            className={`space-y-2 rounded-xl border p-4 ${
              isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span
                className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}
              >
                Recent health activity
              </span>
            </div>
            <RecentHealthEvents events={provider.recentHealthEvents ?? []} />
          </div>
        )}
      </div>
      {/* 2026-07-05: portalled above the edit modal — React portal insertion order
          puts it on top, its own backdrop click closes only it, and the outer form
          keeps its state so the operator returns to exactly what they typed. */}
      {isEdit && provider && (
        <BrowseUpstreamModelsModal
          isOpen={browseOpen}
          providerId={provider.id}
          providerName={provider.displayName}
          onClose={() => setBrowseOpen(false)}
        />
      )}
    </Modal>
  );
}
